import type {
  PackagingHeavyMetalsAuditVerdict,
  PackagingSvhcAuditVerdict,
} from '@/shared/hooks/api/packagingSuppliers'

export type PackagingAuditKind = 'submission' | 'heavy_metals' | 'svhc'

export type PackagingAuditManualOverrideMeta = {
  fromResult: string
  toResult: string
  reason: string
  overriddenByUserId: string
}

export const SUBMISSION_AUDIT_RESULT_OPTIONS = ['Clear', 'Flagged'] as const

export type SubmissionAuditResultOption =
  (typeof SUBMISSION_AUDIT_RESULT_OPTIONS)[number]

export const PACKAGING_HEAVY_METALS_AUDIT_VERDICT_OPTIONS = [
  'TEST_REPORT_REDUCED_SUFFICIENTLY',
  'TEST_REPORT_PRESENT_NOT_REDUCED_SUFFICIENTLY',
  'TEST_REPORT_PRESENT_MATERIAL_MISMATCH',
  'TEST_REPORT_PRESENT_DEFICIENT',
  'STATEMENT',
  'STATEMENT_PRESENT_NOT_RELEVANT',
  'STATEMENT_IS_TEST_REPORT_REDUCED_SUFFICIENTLY',
  'STATEMENT_IS_TEST_REPORT_NOT_REDUCED_SUFFICIENTLY',
  'STATEMENT_IS_TEST_REPORT_MATERIAL_MISMATCH',
  'STATEMENT_IS_TEST_REPORT_NOT_RELEVANT',
  'TEST_REPORT_NOT_PRESENT',
] as const satisfies readonly PackagingHeavyMetalsAuditVerdict[]

export const PACKAGING_SVHC_AUDIT_VERDICT_OPTIONS = [
  'SVHC_TEST_REPORT_SVHC_NEGATIVE',
  'SVHC_TEST_REPORT_SVHC_POSITIVE',
  'SVHC_TEST_REPORT_INCONCLUSIVE',
  'SVHC_TEST_REPORT_NOT_PRESENT',
  'SVHC_STATEMENT',
  'SVHC_STATEMENT_PRESENT_NOT_RELEVANT',
] as const satisfies readonly PackagingSvhcAuditVerdict[]

export function countWords(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length
}

/**
 * Type guard: narrows a stored audit history entry union to its manual-override
 * branch. AI entries have no `source` literal, so checking `source` narrows
 * both ways — `if (isManualOverrideEntry(e))` / `if (!isManualOverrideEntry(e))`.
 */
export function isManualOverrideEntry<
  TManual extends { source: 'manual_override' },
  TAi extends object,
>(entry: TManual | TAi): entry is TManual {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    (entry as { source?: unknown }).source === 'manual_override'
  )
}

export type ManualOverrideValidationMessages = {
  reasonRequired: string
  reasonTooLong: string
  differentResultRequired: string
}

export function validateManualOverrideForm(input: {
  currentResult: string | null
  newResult: string
  reason: string
  messages: ManualOverrideValidationMessages
}): {
  canSave: boolean
  reasonError: string | null
  resultError: string | null
} {
  const wordCount = countWords(input.reason)
  let reasonError: string | null = null
  if (wordCount < 1) {
    reasonError = input.messages.reasonRequired
  } else if (wordCount > 300) {
    reasonError = input.messages.reasonTooLong
  }

  let resultError: string | null = null
  if (
    input.currentResult != null &&
    input.newResult !== '' &&
    input.newResult === input.currentResult
  ) {
    resultError = input.messages.differentResultRequired
  }

  const canSave =
    input.newResult.trim() !== '' &&
    reasonError == null &&
    resultError == null &&
    input.currentResult != null &&
    input.newResult !== input.currentResult

  return {
    canSave,
    reasonError,
    resultError,
  }
}
